import {
    Box,
    Button,
    Paper,
    Chip,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Typography,
    Avatar,
    Fade,
    useTheme,
    useMediaQuery
} from '@mui/material'
import {
    Add,
    AccountBalanceWallet,
    CreditCardOff,
    LockOpen,
    AcUnit,
    Assignment,
    CheckCircle,
    VpnKey,
    AutoAwesome,
    Security,
    Speed,
    TrendingUp,
    Shield,
    FlashOn,
    Star,
    MoreVert
} from '@mui/icons-material'
import { useState } from 'react'
import { ConfirmationModal } from '../../../../components/ConfirmationModal'
import {
    CardStatementAction,
    FreezeCardAction,
    OpenCardAction,
    SetPasswordAction,
    CreateVirtualCardAction,
    CreatePhysicalCardAction,
    AddOrActivateCardModal,
} from './actions';
import { usePermission } from '../../../../hooks/usePermission'
import { enqueueSnackbar } from 'notistack';

const ActionButtons = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Tüm kart işlem butonları tek permission'a bağlı (tek istek)
    const { hasPermission: canCardOperations } = usePermission({
        code: 'Corporate.POST.Updating.Kartİşlemleri'
    });

    // Kart listeleme yetkisi yoksa bazı aksiyonlar engellenir ve uyarı gösterilir
    const { hasPermission: canViewCardList, isLoading: isCardListPermissionLoading } = usePermission({
        code: 'Corporate.POST.Reading.KartListeleme'
    });


    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        content: '',
        action: '',
        isLoading: false
    })
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null)
    const dropdownOpen = Boolean(anchorEl)
    const actionsMenuOpen = Boolean(actionsAnchorEl)

    // Action modal states
    const [openCardModal, setOpenCardModal] = useState(false);
    const [freezeCardModal, setFreezeCardModal] = useState(false);
    const [cardStatementModal, setCardStatementModal] = useState(false);
    const [addOrActivateCardModal, setAddOrActivateCardModal] = useState(false);
    const [setPasswordModal, setSetPasswordModal] = useState(false);
    const [createVirtualCardModal, setCreateVirtualCardModal] = useState(false);
    const [createPhysicalCardModal, setCreatePhysicalCardModal] = useState(false);

    const openNoListPermissionWarning = () => {
        enqueueSnackbar(
            'Kart listesi görüntüleme yetkiniz bulunmadığından bu işlem gerçekleştirilememektedir.',
            { variant: 'warning' }
        );
    };

    const handleGuardedConfirmAction = (action: string, title: string, content: string) => {
        // Kart listesi görüntüleme yetkisi yoksa aksiyon modalını açma
        if (!isCardListPermissionLoading && !canViewCardList) {
            openNoListPermissionWarning();
            return;
        }

        handleConfirmAction(action, title, content);
    };

    const handleConfirmAction = (action: string, title: string, content: string) => {
        switch (action) {
             case 'unlock':
                 setOpenCardModal(true);
                 break;
             case 'freeze':
                 setFreezeCardModal(true);
                 break;
            case 'statement':
                setCardStatementModal(true);
                break;
            case 'addOrActivate':
                setAddOrActivateCardModal(true);
                break;
            case 'setPassword':
                setSetPasswordModal(true);
                break;
            case 'createVirtualCard':
                setCreateVirtualCardModal(true);
                break;
            case 'createPhysicalCard':
                setCreatePhysicalCardModal(true);
                break;
            default:
                // Diğer aksiyonlar için eski modal sistemini kullan
                setConfirmModal({
                    isOpen: true,
                    title,
                    content,
                    action,
                    isLoading: false
                });
        }
    }

    // Success callback for all actions
    const handleActionSuccess = () => {
        // Burada kartları yeniden yükleyebilirsiniz
    };

    const handleDropdownClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleDropdownClose = () => {
        setAnchorEl(null)
    }

    const handleActionsMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setActionsAnchorEl(event.currentTarget)
    }

    const handleActionsMenuClose = () => {
        setActionsAnchorEl(null)
    }

    const handleModalConfirm = () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }))

        // Simulate API call
        setTimeout(() => {
            setConfirmModal({
                isOpen: false,
                title: '',
                content: '',
                action: '',
                isLoading: false
            })
        }, 2000)
    }

    const handleModalClose = () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }))
    }

    const actionButtons = [
        {
            label: 'Kartı Aç',
            subtitle: 'Kart kullanımını aktifleştir',
            icon: <LockOpen />,
            color: 'success' as const,
            gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            shadowColor: 'rgba(16, 185, 129, 0.4)',
            variant: 'outlined' as const,
            badgeIcon: <Shield />,
            action: () => handleGuardedConfirmAction(
                'unlock',
                'Kartı Aç',
                'Seçili kartları açmak istediğinizden emin misiniz?'
            ),
            permission: canCardOperations
        },
        {
            label: 'Kartı Dondur',
            subtitle: 'Geçici kullanım durdur',
            icon: <AcUnit />,
            color: 'warning' as const,
            gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            shadowColor: 'rgba(245, 158, 11, 0.4)',
            variant: 'outlined' as const,
            badgeIcon: <Security />,
            action: () => handleGuardedConfirmAction(
                'freeze',
                'Kartı Dondur',
                'Seçili kartları dondurmak istediğinizden emin misiniz? Bu işlem geçicidir.'
            ),
            permission: canCardOperations
        },
        {
            label: 'Kart Ekstresi',
            subtitle: 'Detaylı işlem raporu',
            icon: <Assignment />,
            color: 'info' as const,
            gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            shadowColor: 'rgba(59, 130, 246, 0.4)',
            variant: 'outlined' as const,
            badgeIcon: <TrendingUp />,
            action: () => handleGuardedConfirmAction(
                'statement',
                'Kart Ekstresi',
                'Seçili kartların ekstresini oluşturmak istediğinizden emin misiniz?'
            ),
            permission: canCardOperations
        },
        {
            label: 'Kart Ekle / Aktive Et',
            subtitle: 'Kart ekleme veya aktivasyon',
            icon: <CheckCircle />,
            color: 'primary' as const,
            gradient: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            shadowColor: 'rgba(30, 60, 114, 0.4)',
            variant: 'outlined' as const,
            badgeIcon: <FlashOn />,
            action: () => handleGuardedConfirmAction(
                'addOrActivate',
                'Kart Ekle / Aktive Et',
                'Kart ekleme veya aktivasyon işlemine devam etmek için modalı kullanın.'
            ),
            permission: canCardOperations
        },
        {
            label: 'Şifre Ata',
            subtitle: 'Güvenli PIN oluştur',
            icon: <VpnKey />,
            color: 'secondary' as const,
            gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            shadowColor: 'rgba(139, 92, 246, 0.4)',
            variant: 'outlined' as const,
            badgeIcon: <Star />,
            action: () => handleGuardedConfirmAction(
                'setPassword',
                'Şifre Ata',
                'Seçili kartlara yeni şifre atamak istediğinizden emin misiniz?'
            ),
            permission: canCardOperations
        }
    ].filter(button => button.permission); // Yetkisi olan butonları göster

    // Eğer hiçbir action button yetkisi yoksa bileşeni gösterme
    if (!canCardOperations || actionButtons.length === 0) {
        return null;
    }

    return (
        <Box>
            {/* Premium Action Buttons Section */}
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, sm: 3 },
                    mb: 3,
                    borderRadius: 3,
                    background: 'rgba(255,255,255,0.7)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(226, 232, 240, 0.8)',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{
                            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                            mr: 2,
                            width: 40,
                            height: 40
                        }}>
                            <AutoAwesome />
                        </Avatar>
                        <Box>
                            <Typography variant="h5" sx={{
                                color: '#1a202c',
                                fontWeight: 700,
                                mb: 0.5,
                                fontSize: '18px !important'
                            }}>
                                Kart İşlemleri
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748b' }}>
                                Tüm kart operasyonlarınızı tek yerden yönetin
                            </Typography>
                        </Box>
                    </Box>
                    {!isMobile && (
                        <Chip
                            icon={<Speed />}
                            label="Hızlı İşlem"
                            sx={{
                                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                                color: 'white',
                                fontWeight: 600,
                                px: 2,
                                '& .MuiChip-icon': { color: 'white' }
                            }}
                        />
                    )}
                </Box>

                {/* Action Buttons Section */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2
                }}>
                    {/* Create Button - "Yeni" + icon on mobile */}
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleDropdownClick}
                        sx={{
                            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            textTransform: 'none',
                            borderRadius: 2,
                            px: { xs: 2, sm: 3 },
                            py: 1.2,
                            flexShrink: 0,
                            boxShadow: '0 4px 12px rgba(30, 60, 114, 0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #1a3366 0%, #245082 100%)',
                                boxShadow: '0 6px 20px rgba(30, 60, 114, 0.4)',
                                transform: 'translateY(-1px)'
                            },
                            transition: 'all 0.3s ease'
                        }}
                    >
                        Yeni Kart Oluştur
                    </Button>

                    {/* Divider - hidden on mobile */}
                    {!isMobile && (
                        <Divider orientation="vertical" flexItem sx={{
                            height: 40,
                            bgcolor: '#cbd5e1',
                            mx: 1
                        }} />
                    )}

                    {/* Action Buttons - dropdown on mobile, normal on desktop */}
                    {isMobile ? (
                        <>
                            <Button
                                variant="outlined"
                                startIcon={<MoreVert />}
                                onClick={handleActionsMenuOpen}
                                sx={{
                                    border: '1px solid #d1d5db',
                                    borderRadius: 2,
                                    px: 2,
                                    py: 1.2,
                                    color: '#64748b',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    textTransform: 'none',
                                    '&:hover': {
                                        background: 'rgba(30, 60, 114, 0.05)',
                                        borderColor: '#1e3c72',
                                        color: '#1e3c72'
                                    }
                                }}
                            >
                                İşlemler
                            </Button>
                            <Menu
                                anchorEl={actionsAnchorEl}
                                open={actionsMenuOpen}
                                onClose={handleActionsMenuClose}
                                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                PaperProps={{
                                    sx: {
                                        mt: 1.5,
                                        minWidth: 220,
                                        borderRadius: 2,
                                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                                    }
                                }}
                            >
                                {actionButtons.map((button, index) => (
                                    <MenuItem
                                        key={index}
                                        onClick={() => {
                                            handleActionsMenuClose();
                                            button.action();
                                        }}
                                        sx={{ py: 1.5, gap: 1.5 }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                                            {button.icon}
                                        </ListItemIcon>
                                        <ListItemText primary={button.label} primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }} />
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    ) : (
                        actionButtons.map((button, index) => (
                            <Button
                                key={index}
                                variant="outlined"
                                startIcon={button.icon}
                                onClick={button.action}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    borderRadius: 2,
                                    px: 2.5,
                                    py: 1.2,
                                    minWidth: 'auto',
                                    flexShrink: 0,
                                    border: '1px solid',
                                    borderColor: button.color === 'info' ? '#3b82f6' :
                                        button.color === 'primary' ? '#1e3c72' :
                                            button.color === 'secondary' ? '#8b5cf6' : '#d1d5db',
                                    color: button.color === 'info' ? '#1d4ed8' :
                                        button.color === 'primary' ? '#1e3c72' : '#7c3aed',
                                    background: 'rgba(255,255,255,0.8)',
                                    backdropFilter: 'blur(10px)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        background: button.color === 'info' ? 'rgba(59, 130, 246, 0.1)' :
                                            button.color === 'primary' ? 'rgba(30, 60, 114, 0.1)' :
                                                button.color === 'secondary' ? 'rgba(139, 92, 246, 0.1)' : 'rgba(209, 213, 219, 0.1)',
                                    }
                                }}
                            >
                                {button.label}
                            </Button>
                        ))
                    )}
                </Box>
            </Paper>

            {/* Premium Dropdown Menu for Create Card */}
            <Menu
                anchorEl={anchorEl}
                open={dropdownOpen}
                onClose={handleDropdownClose}
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                TransitionComponent={Fade}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        mt: 2,
                        borderRadius: 3,
                        minWidth: 320,
                        background: 'rgba(255,255,255,0.95)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 50%, #1e3c72 100%)',
                        }
                    }
                }}
            >
                {/* Menu Header */}
                <Box sx={{ p: 3, pb: 1 }}>
                    <Typography variant="h5" sx={{
                        fontWeight: 700,
                        color: '#1a202c',
                        mb: 0.5
                    }}>
                        Yeni Kart Oluştur
                    </Typography>
                </Box>

                <Divider sx={{ mx: 2, opacity: 0.5 }} />

                {/* Virtual Card Option */}
                <MenuItem
                    onClick={() => {
                        handleDropdownClose()
                        handleConfirmAction('createVirtualCard', 'Sanal Kart Oluştur', 'Sanal kart oluşturmak istiyor musunuz?')
                    }}
                    sx={{
                        p: 2,
                        m: 1,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, rgba(42, 82, 152, 0.05) 0%, rgba(30, 60, 114, 0.1) 100%)',
                        border: '1px solid rgba(42, 82, 152, 0.1)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            background: 'linear-gradient(135deg, rgba(42, 82, 152, 0.1) 0%, rgba(30, 60, 114, 0.15) 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(42, 82, 152, 0.2)',
                        }
                    }}
                >
                    <ListItemIcon>
                        <Avatar sx={{
                            background: 'linear-gradient(135deg, #2a5298 0%, #1e3c72 100%)',
                            color: 'white',
                            width: 48,
                            height: 48
                        }}>
                            <AccountBalanceWallet />
                        </Avatar>
                    </ListItemIcon>
                    <ListItemText
                        primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a202c', ml: 2 }}>
                                    Sanal Kart Oluştur
                                </Typography>
                                {/* <Chip
                                    label="Hızlı"
                                    size="small"
                                    sx={{
                                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '0.7rem'
                                    }}
                                /> */}
                            </Box>
                        }
                    /*  secondary={
                         <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                             ⚡ Anlık kullanım için • Online ödemeler • Güvenli alışveriş
                         </Typography>
                     } */
                    />
                </MenuItem>

                {/* Physical Card Option */}
                <MenuItem
                    onClick={() => {
                        handleDropdownClose()
                        handleConfirmAction('createPhysicalCard', 'Fiziki Kart Oluştur', 'Fiziki kart oluşturmak istiyor musunuz?')
                    }}
                    sx={{
                        p: 2,
                        m: 1,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(124, 58, 237, 0.1) 100%)',
                        border: '1px solid rgba(139, 92, 246, 0.1)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.15) 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(139, 92, 246, 0.2)',
                        }
                    }}
                >
                    <ListItemIcon>
                        <Avatar sx={{
                            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                            color: 'white',
                            width: 48,
                            height: 48
                        }}>
                            <CreditCardOff />
                        </Avatar>
                    </ListItemIcon>
                    <ListItemText
                        primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a202c' }}>
                                    Fiziki Kart Oluştur
                                </Typography>
                            </Box>
                        }

                    />
                </MenuItem>
            </Menu>

            {/* Action Modals */}
             <OpenCardAction
                isOpen={openCardModal}
                onClose={() => setOpenCardModal(false)}
                onSuccess={handleActionSuccess}
            />

            <FreezeCardAction
                isOpen={freezeCardModal}
                onClose={() => setFreezeCardModal(false)}
                onSuccess={handleActionSuccess}
            /> 

            <CardStatementAction
                isOpen={cardStatementModal}
                onClose={() => setCardStatementModal(false)}
                onSuccess={handleActionSuccess}
            />

            <AddOrActivateCardModal
                isOpen={addOrActivateCardModal}
                onClose={() => setAddOrActivateCardModal(false)}
                onSuccess={handleActionSuccess}
            />

            <SetPasswordAction
                isOpen={setPasswordModal}
                onClose={() => setSetPasswordModal(false)}
                onSuccess={handleActionSuccess}
            />

            <CreateVirtualCardAction
                isOpen={createVirtualCardModal}
                onClose={() => setCreateVirtualCardModal(false)}
                onSuccess={handleActionSuccess}
            />

            <CreatePhysicalCardAction
                isOpen={createPhysicalCardModal}
                onClose={() => setCreatePhysicalCardModal(false)}
                onSuccess={handleActionSuccess}
            />

            {/* Confirmation Modal for other actions */}
            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                onClose={handleModalClose}
                onConfirm={handleModalConfirm}
                title={confirmModal.title}
                content={confirmModal.content}
                isLoading={confirmModal.isLoading}
                confirmButtonText="Onayla"
                closeButtonText="İptal"
            />
        </Box>
    )
}

export default ActionButtons